import { useEvent } from "shared/lib/useEvent"
import { UpdateFollowUpConfigurationInput } from "shared/types/__generated__/graphql"

import { MoveDirection } from "../../../shared/types/MoveDirection"
import { FollowUpConfigurationGroupData } from "../types/FollowUpConfigurationGroupData"

interface UseFollowUpConfigurationGroupViewModelProps {
  group: FollowUpConfigurationGroupData
  onUpdate: (id: string, input: UpdateFollowUpConfigurationInput) => void
}

// Position math assumes group.entries is the complete, unfiltered set for this
// source product — true whenever move/reorder can actually fire, since the
// page disables both controls while a filter is active.
export function useFollowUpConfigurationGroupViewModel({
  group,
  onUpdate
}: UseFollowUpConfigurationGroupViewModelProps) {
  const handleMove = useEvent((id: string, direction: MoveDirection) => {
    const entry = group.entries.find(candidate => candidate.id === id)
    if (!entry) return

    const newPosition =
      direction === MoveDirection.UP ? entry.position - 1 : entry.position + 1
    if (newPosition < 0 || newPosition >= group.entries.length) return

    onUpdate(id, { position: newPosition })
  })

  const handleReorder = useEvent(
    (entryID: string, targetEntryID: string, insertBefore: boolean) => {
      if (entryID === targetEntryID) return

      const entry = group.entries.find(candidate => candidate.id === entryID)
      const targetEntry = group.entries.find(
        candidate => candidate.id === targetEntryID
      )
      if (!entry || !targetEntry) return

      const insertPosition =
        targetEntry.position -
        (targetEntry.position > entry.position ? 1 : 0) +
        (insertBefore ? 0 : 1)
      if (insertPosition === entry.position) return

      onUpdate(entryID, { position: insertPosition })
    }
  )

  return { handleMove, handleReorder }
}
